import { useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControl, FormControlLabel, IconButton, InputAdornment, MenuItem, Select, Stack, Typography } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useCommonStyles } from '../../styles/GenericStyle';
import { EventsReportStyleObj, materialDownloadBtnDisabled } from '../../styles/EventsReportStyleObj';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useExportS2sEventsReport, useExportS2sReport } from '../../api/vegApl';
import { EVENTS_REPORT_RANGE_OPTIONS, GenericConstant } from '../../utils/GenericConstant';

const EventsReport = () => {
    const navigate = useNavigate();
    const classes = useCommonStyles();
    const { showSnackbar } = useSnackbar();
    const [reportDateRange, setReportDateRange] = useState<[Dayjs | null, Dayjs | null]>([dayjs(), dayjs()]);
    const [selectedRange, setSelectedRange] = useState<string>('today');
    const [isLoading, setIsLoading] = useState(false);
    const [isMaterialLoading, setIsMaterialLoading] = useState(false);
    const [useCustomDateRange, setUseCustomDateRange] = useState(false);

    const canSubmit = useMemo(() => {
        const [startDate, endDate] = reportDateRange;
        if (useCustomDateRange) {
            return !!startDate && startDate.isValid() && !!endDate && endDate.isValid() && !isLoading;
        }
        return !!selectedRange && !isLoading;
    }, [reportDateRange, selectedRange, useCustomDateRange, isLoading]);

    const buildTodayFileName = (prefix: string) => {
        const now = new Date();
        return `${prefix}-${format(now, 'yyyy-MM-dd')}.xlsx`;
    };

    const downloadBlobWithName = (blobData: any, fileName: string) => {
        const blob = blobData instanceof Blob
            ? blobData
            : new Blob([blobData]);
        const href = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(href);
    };

    const { mutate: exportEventsReport } = useExportS2sEventsReport({
        mutation: {
            onSuccess: (response: any) => {
                downloadBlobWithName(response?.data, buildTodayFileName('events-report'));
                showSnackbar({ message: 'Events report downloaded successfully', severity: 'success' });
                setIsLoading(false);
            },
            onError: (error: any) => {
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Failed to download events report', severity: 'error' });
                setIsLoading(false);
            },
        },
    });

    const { mutate: exportS2sReport } = useExportS2sReport({
        mutation: {
            onSuccess: (response: any) => {
                downloadBlobWithName(response?.data, buildTodayFileName('s2s-report'));
                showSnackbar({ message: 'S2S report downloaded successfully', severity: 'success' });
                setIsMaterialLoading(false);
            },
            onError: (error: any) => {
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Failed to download S2S report', severity: 'error' });
                setIsMaterialLoading(false);
            },
        },
    });

    const handleMaterialDownload = () => {
        setIsMaterialLoading(true);
        exportS2sReport();
    };

    const handleDownload = () => {
        const [startDate, endDate] = reportDateRange;
        if (useCustomDateRange) {
            if (!startDate || !startDate.isValid() || !endDate || !endDate.isValid()) {
                showSnackbar({ message: 'Please select a date range', severity: 'warning' });
                return;
            }
            setIsLoading(true);
            exportEventsReport({
                data: {
                    start_date: startDate!.format('YYYY-MM-DD'),
                    end_date: endDate!.format('YYYY-MM-DD'),
                },
            } as any);
            return;
        }

        if (!selectedRange) {
            showSnackbar({ message: 'Please select a range', severity: 'warning' });
            return;
        }

        setIsLoading(true);
        exportEventsReport({
            data: {
                range: selectedRange,
            },
        } as any);
    };

    return (
        <div data-testid="EventsReportPage">
            <Box sx={EventsReportStyleObj.pageContainer}>
                <Box sx={EventsReportStyleObj.backRow} onClick={() => navigate(-1)}>
                    <ArrowBackIcon sx={EventsReportStyleObj.backIcon} />
                    <Typography className={classes.labelNoto20Bold600} sx={EventsReportStyleObj.backLabel}>Back</Typography>
                </Box>

                <Box sx={EventsReportStyleObj.titleRow}>
                    <Typography
                        className={classes.labelNoto36Bold700}
                        sx={EventsReportStyleObj.title}
                    >
                        Download Reports
                    </Typography>
                </Box>

                <Box sx={EventsReportStyleObj.card}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={{ xs: 1.5, sm: 0 }}
                        sx={EventsReportStyleObj.sectionHeaderRow}
                    >
                        <Box sx={EventsReportStyleObj.sectionTextWrap}>
                            <Typography className={classes.labelNoto20Bold600} sx={EventsReportStyleObj.sectionTitle}>
                                Material Report
                            </Typography>
                            <Typography className={classes.labelInter14Bold400} sx={EventsReportStyleObj.sectionSubtitle}>
                                Download list of all materials with its details
                            </Typography>
                        </Box>

                        <Button
                            variant="contained"
                            disableRipple
                            className={classes.greenButton}
                            sx={materialDownloadBtnDisabled}
                            disabled={isMaterialLoading}
                            onClick={handleMaterialDownload}
                        >
                            {isMaterialLoading ? 'Downloading...' : 'Download'}
                        </Button>
                    </Stack>

                    <Stack sx={EventsReportStyleObj.eventRow}>
                        <Stack direction="column" spacing={1} sx={EventsReportStyleObj.eventFormStack}>
                            <Typography className={classes.labelNoto20Bold600} sx={EventsReportStyleObj.sectionTitle}>
                                Event Report
                            </Typography>
                            <Typography className={classes.labelInter14Bold400} sx={EventsReportStyleObj.sectionSubtitle}>
                                Download list of all material events
                            </Typography>
                            <FormControlLabel
                                sx={EventsReportStyleObj.dateRangeToggle}
                                control={
                                    <Checkbox
                                        checked={useCustomDateRange}
                                        onChange={(e) => setUseCustomDateRange(e.target.checked)}
                                        disableRipple
                                        sx={{ ...GenericConstant.CHECKBOXSTYLE, ...EventsReportStyleObj.checkbox }}
                                        checkedIcon={<Box component="img" alt="" src={"icons/CheckBox.svg"} sx={EventsReportStyleObj.checkboxIcon} />}
                                    />
                                }
                                label={
                                    <Typography className={classes.labelInter14Bold400} sx={EventsReportStyleObj.customDateLabel}>
                                        Custom Date Range
                                    </Typography>
                                }
                            />

                            {!useCustomDateRange && (
                                <Box sx={EventsReportStyleObj.rangeFieldContainer}>
                                    <Typography className={classes.labelInter14Bold400} sx={EventsReportStyleObj.fieldLabel}>
                                        Range
                                    </Typography>
                                    <FormControl size="small" sx={EventsReportStyleObj.fullWidth}>
                                        <Select
                                            value={selectedRange}
                                            onChange={(e) => setSelectedRange(e.target.value)}
                                            displayEmpty
                                            sx={EventsReportStyleObj.selectField}
                                            MenuProps={GenericConstant.PAPERSTYLE}
                                        >
                                            {EVENTS_REPORT_RANGE_OPTIONS.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}

                            {useCustomDateRange && (
                                <Box sx={EventsReportStyleObj.rangeFieldContainer}>
                                    <Typography className={classes.labelInter14Bold400} sx={EventsReportStyleObj.fieldLabel}>
                                        Select Date Range
                                    </Typography>
                                    <FormControl size="small" sx={EventsReportStyleObj.fullWidth}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <DateRangePicker
                                                slots={{ field: SingleInputDateRangeField }}
                                                format="YYYY/MM/DD"
                                                value={reportDateRange}
                                                onChange={(newValue) => setReportDateRange(newValue as [Dayjs | null, Dayjs | null])}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        placeholder: 'Select Date range',
                                                        fullWidth: true,
                                                        InputProps: {
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        aria-label="Open end date picker"
                                                                        edge="end"
                                                                        size="small"
                                                                        disableRipple
                                                                        disableFocusRipple
                                                                        sx={EventsReportStyleObj.dateAdornmentIconBtn}
                                                                    >
                                                                        <img src="icons/calendar.svg" alt="Information" style={EventsReportStyleObj.dateAdornmentImage} />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            ),
                                                        },
                                                    },
                                                }}
                                                sx={EventsReportStyleObj.dateRangeField}
                                            />
                                        </LocalizationProvider>
                                    </FormControl>
                                </Box>
                            )}
                        </Stack>

                        <Button
                            variant="contained"
                            disableRipple
                            className={classes.greenButton}
                            sx={EventsReportStyleObj.eventsDownloadBtnDisabled}
                            disabled={!canSubmit}
                            onClick={handleDownload}
                        >
                            {isLoading ? 'Downloading...' : 'Download'}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </div>
    );
};

export default EventsReport;
